import "assets/scss/customers.scss";
import { customersAPI } from "components/Apis/customers";
import RevenueProfitAllCustomers from "components/Customers/allCustomersMarginGraph";
import CustomerDetailView from "components/Customers/customerDetailView";
import { shortCurrencyFormatter } from "components/KPIs/helpers";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Collapse } from "reactstrap";
import { fetchCustomerMargins } from "redux/analytics/customerSlice";
import { useDebouncedValue } from "utils/logic";

const Customers = () => {
    const dispatch = useDispatch();

    // dates: last 365 days
    const [startDate, setStartDate] = useState(
        new Date(new Date().setDate(new Date().getDate() - 365)).toISOString().slice(0, 10)
    );
    const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

    const [ordering, setOrdering] = useState("-revenue");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [totalPages, setTotalPages] = useState(1);

    // raw (immediate) search input + debounced value
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearch = useDebouncedValue(searchTerm, 450);

    // open customer rows
    const [openRows, setOpenRows] = useState({});
    const [customerDetails, setCustomerDetails] = useState({});


    // get data from redux
    const { error: customersError, loading: customersLoading, data: customers } = useSelector(
        (state) => state.customers.customerMargins
    );



    // fetch when filters change (debouncedSearch drives the query)
    useEffect(() => {
        dispatch(
            fetchCustomerMargins({
                startDate,
                endDate,
                page,
                pageSize,
                ordering,
                searchTerm: debouncedSearch,
            })
        );
    }, [startDate, endDate, page, pageSize, ordering, debouncedSearch, dispatch]);

    // reset to page 1 if filters (except page) change
    useEffect(() => {
        setPage(1);
        setOpenRows({});
        setCustomerDetails({});
    }, [debouncedSearch, startDate, endDate, ordering, pageSize]);

    // update total pages from server count
    useEffect(() => {
        const count = customers?.count ?? 0;
        setTotalPages(count ? Math.ceil(count / pageSize) : 1);
    }, [customers, pageSize]);

    const fetchCustomerDetail = useCallback(
        async (customerId) => {
            if (!customerId) return;
            setCustomerDetails((prev) => ({
                ...prev,
                [customerId]: {
                    data: prev[customerId]?.data,
                    loading: true,
                    error: null,
                },
            }));

            try {
                const response = await customersAPI.getCustomer(customerId, startDate, endDate);
                setCustomerDetails((prev) => ({
                    ...prev,
                    [customerId]: {
                        data: response.data,
                        loading: false,
                        error: null,
                    },
                }));
            } catch (err) {
                const message = err?.response?.data?.detail || err?.message || 'Unable to load customer detail.';
                setCustomerDetails((prev) => ({
                    ...prev,
                    [customerId]: {
                        data: prev[customerId]?.data,
                        loading: false,
                        error: message,
                    },
                }));
            }
        },
        [startDate, endDate]
    );

    const handleToggleRow = useCallback(
        (customer) => {
            if (!customer) return;
            const isOpen = !!openRows[customer.id];
            const willOpen = !isOpen;
            setOpenRows((prev) => ({ ...prev, [customer.id]: willOpen }));

            if (willOpen) {
                const detailState = customerDetails[customer.id];
                const needsFetch = !detailState || (!detailState.data && !detailState.loading);
                if (needsFetch) {
                    fetchCustomerDetail(customer.id);
                }
            }
        },
        [customerDetails, fetchCustomerDetail, openRows]
    );

    return (
        <div style={{ marginTop: -10 }}>
            <div style={{ display: "flex", width: "100%", justifyContent: "space-between", marginBottom: 20, gap: 20 }}>
                <div style={{ flexGrow: 1 }}>
                    <label>Search</label>
                    <input
                        className="input thin form-control"
                        type="text"
                        placeholder="Search Customers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div style={{ flexWrap: "nowrap", display: "flex", gap: 10 }}>
                    <div style={{ flexDirection: "column" }}>
                        <label>Start Date: </label>
                        <input
                            className="input thin form-control"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div style={{ flexDirection: "column" }}>
                        <label>End Date: </label>
                        <input
                            className="input thin form-control  "
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <hr />
            {
                (
                    () => {
                        const cleanOrdering = ordering.replace("-", "").replace("_", " ");
                        return (
                            <p className="text-muted mb-0">Customers — revenue, profit and load count</p>
                        );
                    }
                )()
            }
            <RevenueProfitAllCustomers
                startDate={startDate}
                endDate={endDate}
                searchTerm={debouncedSearch}
                ordering={ordering}
            />
            <hr />

            <div className="pulse-section">
                <table className="briefing-table all-orders-table customers-table">
                    {(() => {
                        const HEADERS = [
                            { label: "Customer", key: "company_name" },
                            { label: "Miles", key: "miles" },
                            { label: "Orders", key: "order_count" },
                            { label: "Revenue", key: "revenue" },
                            { label: "Profit", key: "profit" },
                            { label: "Margin", key: "margin" },
                        ];
                        return (
                            <thead>
                                <tr>
                                    {HEADERS.map((header) => (
                                        <th
                                            key={header.key}
                                            onClick={() => {
                                                let newOrdering = header.key;
                                                if (ordering === header.key) newOrdering = `-${header.key}`;
                                                setOrdering(newOrdering);
                                            }}
                                            style={{ cursor: "pointer" }}
                                        >
                                            {header.label}
                                            {ordering === header.key
                                                ? " ▲"
                                                : ordering === `-${header.key}`
                                                    ? " ▼"
                                                    : ""}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                        );
                    })()}
                    <tbody>
                        {customers?.results?.map((customer, index) => (
                            <>
                                <tr key={index}>
                                    <td style={{ width: "35%" }}>
                                        <span
                                            style={{ cursor: "pointer", color: "#007bff" }}
                                            onClick={() => handleToggleRow(customer)}
                                        >
                                            {customer.company_name}
                                        </span>
                                    </td>
                                    <td>{customer.miles}</td>
                                    <td>{customer.order_count}</td>
                                    <td>{shortCurrencyFormatter(customer.revenue)}</td>
                                    <td>{shortCurrencyFormatter(customer.profit)}</td>
                                    <td>{customer.margin}%</td>
                                </tr>
                                <tr className="customer-detail-row" style={{ padding: 0, margin: 0, border: 'none' }}>
                                    <td colSpan={6} style={{ padding: 0, margin: 0, border: 'none' }}>
                                        <Collapse isOpen={openRows[customer.id]}>
                                            <CustomerDetailView
                                                summary={customer}
                                                detail={customerDetails[customer.id]?.data}
                                                loading={customerDetails[customer.id]?.loading}
                                                error={customerDetails[customer.id]?.error}
                                            />
                                            <hr style={{margin: "10px 30px"}}/>
                                            <br />
                                        </Collapse>
                                    </td>
                                </tr>
                                <tr>
                                </tr>
                            </>
                        ))}
                    </tbody>
                </table>

                {/* pagination */}
                <div className="pager">
                    <div className="pager__left">
                        <span className="pager__range">
                            {(() => {
                                const count = customers?.count ?? 0;
                                if (!count) return "No results";
                                const start = (page - 1) * pageSize + 1;
                                const end = Math.min(page * pageSize, count);
                                return `Showing ${start}–${end} of ${count}`;
                            })()}
                        </span>

                        <div className="pager__nav" role="navigation" aria-label="Pagination">
                            <button className="pager__btn" onClick={() => setPage(1)} disabled={page <= 1} title="First page">
                                <i className="fa fa-angle-double-left" />
                            </button>
                            <button
                                className="pager__btn"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                title="Previous page"
                            >
                                <i className="fa fa-chevron-left" />
                            </button>

                            <div className="pager__status" aria-live="polite">
                                <input
                                    min={1}
                                    max={totalPages}
                                    value={page}
                                    onChange={(e) => {
                                        const v = Number(e.target.value);
                                        if (!Number.isNaN(v)) setPage(Math.max(1, Math.min(totalPages || 1, v)));
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            const v = Number(e.currentTarget.value);
                                            setPage(Math.max(1, Math.min(totalPages || 1, v)));
                                            e.currentTarget.blur();
                                        }
                                    }}
                                    className="pager__input"
                                    aria-label="Current page"
                                />
                                <span className="pager__total">/ {totalPages}</span>
                            </div>

                            <button
                                className="pager__btn"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                title="Next page"
                            >
                                <i className="fa fa-chevron-right" />
                            </button>
                            <button
                                className="pager__btn"
                                onClick={() => setPage(totalPages)}
                                disabled={page >= totalPages}
                                title="Last page"
                            >
                                <i className="fa fa-angle-double-right" />
                            </button>
                        </div>
                    </div>

                    <div className="pager__right">
                        <label className="pager__label" htmlFor="page-size">
                            Rows:
                        </label>
                        <select
                            id="page-size"
                            className="pager__select"
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            aria-label="Rows per page"
                        >
                            {[25, 50, 100, 150, 200].map((sz) => (
                                <option key={sz} value={sz}>
                                    {sz}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Customers;
